/**
 * The account block's filter (DAT-9223): one Cloudscape PropertyFilter over the block's four
 * fields, with one level of AND / OR grouping. Replaces the TextFilter that searched only the
 * page on screen, and only account names — every condition is now applied by the service, over
 * every page, and opportunity names are searchable.
 *
 * Owns what may be sent: a change the service (or the WAF in front of it) would refuse is not
 * passed up — the filter stays at the last state that can be sent, and an Alert says why. Query
 * state, translation and the candidate values belong to the caller.
 */
import { useState } from 'react';
import { Alert, PropertyFilter, SpaceBetween, type PropertyFilterProps } from '@amzn/awsui-components-react';
import FloatingLabelField from '../../../components/FloatingLabelField/FloatingLabelField';
import { PROPERTY_FILTER_I18N } from '../../../components/pipelineFilterShared';
import {
  formatHierarchy,
  formatStage,
  optionsForQueryValues,
  REJECTION_TEXT,
  rejectionOf,
  type FilterRejection,
} from '../accountPipelineFilter';

const enumOperators = (format?: (value: unknown) => string): PropertyFilterProps.FilteringProperty['operators'] => [
  { operator: '=', tokenType: 'enum', ...(format && { format }) },
  { operator: '!=', tokenType: 'enum', ...(format && { format }) },
];

/** `key` is the wire field; see accountPipelineFilter.ACCOUNT_PIPELINE_FIELDS. */
const FILTERING_PROPERTIES: readonly PropertyFilterProps.FilteringProperty[] = [
  { key: 'SFDC_STAGE', propertyLabel: 'Pipeline Status', groupValuesLabel: 'Pipeline Status values', operators: enumOperators(formatStage), defaultOperator: '=' },
  { key: 'ACCOUNT_NAME', propertyLabel: 'Account Name', groupValuesLabel: 'Account Name values', operators: enumOperators(), defaultOperator: '=' },
  { key: 'OPP_NAME', propertyLabel: 'Opportunity Name', groupValuesLabel: 'Opportunity Name values', operators: enumOperators(), defaultOperator: '=' },
  // The ACCOUNT's own sales hierarchy, not the opportunity's.
  { key: 'ACCOUNT_HIERARCHY', propertyLabel: 'Account Hierarchy', groupValuesLabel: 'Account Hierarchy values', operators: enumOperators(formatHierarchy), defaultOperator: '=' },
];

export interface AccountPipelinePropertyFilterProps {
  lighthouse: boolean;
  query: PropertyFilterProps.Query;
  onChange: (query: PropertyFilterProps.Query) => void;
  filteringOptions: readonly PropertyFilterProps.FilteringOption[];
  filteringStatusType: PropertyFilterProps.StatusType;
  onLoadItems: PropertyFilterProps['onLoadItems'];
}

export function AccountPipelinePropertyFilter({
  lighthouse,
  query,
  onChange,
  filteringOptions,
  filteringStatusType,
  onLoadItems,
}: AccountPipelinePropertyFilterProps) {
  const [rejected, setRejected] = useState<FilterRejection | null>(null);
  const ariaLabel = lighthouse ? '筛选灯塔客户' : '筛选圈外客户';
  // The candidates, plus whatever the query already holds (see optionsForQueryValues).
  const options = [...filteringOptions, ...optionsForQueryValues(query, filteringOptions)];
  return (
    <SpaceBetween size="xs">
      <FloatingLabelField label="Filter">
        <PropertyFilter
          query={query}
          onChange={({ detail }) => {
            const why = rejectionOf(detail);
            setRejected(why);
            if (why === null) onChange(detail);
          }}
          filteringProperties={FILTERING_PROPERTIES}
          filteringOptions={options}
          filteringStatusType={filteringStatusType}
          onLoadItems={onLoadItems}
          enableTokenGroups
          expandToViewport
          // Every field is picked from candidates; a free-text "All properties" search would
          // be a fifth field the service does not have.
          disableFreeTextFiltering
          filteringPlaceholder="Filter by Status / Account / Opportunity / Hierarchy"
          filteringAriaLabel={ariaLabel}
          i18nStrings={{ ...PROPERTY_FILTER_I18N, filteringAriaLabel: ariaLabel }}
          filteringLoadingText="Loading options"
          filteringErrorText="Error fetching options"
          filteringRecoveryText="Retry"
          filteringFinishedText="End of results"
        />
      </FloatingLabelField>
      {rejected && (
        <Alert type="warning" dismissible onDismiss={() => setRejected(null)}>
          {REJECTION_TEXT[rejected]}
        </Alert>
      )}
    </SpaceBetween>
  );
}
